import React, { useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

const getPriorityLabel = (tip) => {
  if (tip.isNew) return 'New';
  if (tip.importance === 'high') return 'Start Here';
  if (tip.importance === 'medium') return 'Important';
  return 'Advanced';
};

const getPriorityStyles = (tip) => {
  if (tip.isNew) {
    return {
      background: 'rgba(255, 107, 53, 0.12)',
      color: '#d95d2c',
      border: '1px solid rgba(255, 107, 53, 0.28)',
    };
  }

  if (tip.importance === 'high') {
    return {
      background: 'var(--liwaza-primary-container)',
      color: 'var(--liwaza-on-primary-container)',
      border: '1px solid rgba(0, 0, 0, 0.06)',
    };
  }

  return {
    background: 'var(--liwaza-surface-container-highest)',
    color: 'var(--liwaza-on-surface-variant)',
    border: '1px solid var(--liwaza-outline-variant)',
  };
};

export default function TipsStrategiesPanel({
  title,
  subtitle,
  categories,
  quickHits,
  showTips,
  onToggle,
  announcement,
  requireAcknowledgement = false,
  hasAcknowledged = true,
  onAcknowledge,
  acknowledgeLabel = 'I have read these new techniques',
}) {
  const defaultExpanded = useMemo(
    () => categories.map((_, index) => index).slice(0, 2),
    [categories]
  );
  const [expandedCategories, setExpandedCategories] = useState(defaultExpanded);

  useEffect(() => {
    setExpandedCategories(defaultExpanded);
  }, [defaultExpanded]);

  const allExpanded =
    categories.length > 0 && expandedCategories.length === categories.length;

  const toggleCategory = (index) => {
    setExpandedCategories((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index]
    );
  };

  const toggleAllCategories = () => {
    setExpandedCategories(allExpanded ? [] : categories.map((_, index) => index));
  };

  return (
    <section style={{ marginBottom: '3rem' }}>
      <div
        style={{
          background: 'linear-gradient(180deg, rgba(109, 76, 224, 0.08), rgba(109, 76, 224, 0.02))',
          border: '1px solid rgba(109, 76, 224, 0.14)',
          borderRadius: '18px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '1rem',
            flexWrap: 'wrap',
            marginBottom: showTips ? '1.25rem' : 0,
          }}
        >
          <div style={{ maxWidth: '760px' }}>
            <h2
              style={{
                color: 'var(--liwaza-on-surface)',
                margin: 0,
                fontSize: '1.9rem',
              }}
            >
              {title}
            </h2>
            <p
              style={{
                color: 'var(--liwaza-on-surface-variant)',
                margin: '0.6rem 0 0 0',
                lineHeight: 1.6,
                fontSize: '0.98rem',
              }}
            >
              {subtitle}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {showTips && (
              <button
                onClick={toggleAllCategories}
                style={{
                  background: 'var(--liwaza-surface)',
                  color: 'var(--liwaza-on-surface)',
                  border: '1px solid var(--liwaza-outline-variant)',
                  padding: '0.65rem 0.95rem',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </button>
            )}
            {requireAcknowledgement && !hasAcknowledged ? (
              <div
                style={{
                  background: 'rgba(255, 107, 53, 0.12)',
                  color: '#d95d2c',
                  border: '1px solid rgba(255, 107, 53, 0.24)',
                  padding: '0.65rem 0.9rem',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                }}
              >
                Required first
              </div>
            ) : (
              <button
                onClick={onToggle}
                style={{
                  background: 'var(--liwaza-primary)',
                  color: 'white',
                  border: 'none',
                  padding: '0.65rem 1rem',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  boxShadow: '0 10px 24px rgba(109, 76, 224, 0.18)',
                }}
              >
                {showTips ? 'Hide Tips' : 'Show Tips'}
              </button>
            )}
          </div>
        </div>

        {announcement && (
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.78)',
              border: '1px solid rgba(109, 76, 224, 0.14)',
              borderRadius: '14px',
              padding: '1rem 1.1rem',
              marginBottom: showTips ? '1rem' : 0,
            }}
          >
            <div
              style={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--liwaza-primary)',
                fontWeight: 700,
                marginBottom: '0.4rem',
              }}
            >
              New Techniques
            </div>
            <p
              style={{
                color: 'var(--liwaza-on-surface-variant)',
                margin: 0,
                lineHeight: 1.55,
                fontSize: '0.92rem',
              }}
            >
              {announcement}
            </p>
          </div>
        )}

        {showTips && quickHits?.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1rem',
            }}
          >
            {quickHits.map((hit, index) => (
              <div
                key={`${hit.title}-${index}`}
                style={{
                  background: 'rgba(255, 255, 255, 0.72)',
                  border: '1px solid rgba(109, 76, 224, 0.12)',
                  borderRadius: '14px',
                  padding: '1rem',
                }}
              >
                <div
                  style={{
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: 'var(--liwaza-primary)',
                    fontWeight: 700,
                    marginBottom: '0.45rem',
                  }}
                >
                  {hit.label}
                </div>
                <div
                  style={{
                    color: 'var(--liwaza-on-surface)',
                    fontWeight: 700,
                    marginBottom: '0.35rem',
                  }}
                >
                  {hit.title}
                </div>
                <p
                  style={{
                    color: 'var(--liwaza-on-surface-variant)',
                    margin: 0,
                    lineHeight: 1.5,
                    fontSize: '0.9rem',
                  }}
                >
                  {hit.content}
                </p>
              </div>
            ))}
          </div>
        )}

      </div>

      {showTips && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          {categories.map((category, index) => {
            const isExpanded = expandedCategories.includes(index);

            return (
              <div
                key={`${category.category}-${index}`}
                style={{
                  background: 'var(--liwaza-surface-container)',
                  border: '1px solid var(--liwaza-outline-variant)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                }}
              >
                <button
                  onClick={() => toggleCategory(index)}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1.1rem 1.25rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.85rem',
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        width: '2.4rem',
                        height: '2.4rem',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--liwaza-primary-container)',
                        color: 'var(--liwaza-primary)',
                        flexShrink: 0,
                      }}
                    >
                      {category.icon}
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.6rem',
                          flexWrap: 'wrap',
                        }}
                      >
                        <h3
                          style={{
                            margin: 0,
                            color: 'var(--liwaza-on-surface)',
                            fontSize: '1.05rem',
                          }}
                        >
                          {category.category}
                        </h3>
                        {category.isNew && (
                          <span
                            style={{
                              background: 'rgba(255, 107, 53, 0.12)',
                              color: '#d95d2c',
                              border: '1px solid rgba(255, 107, 53, 0.22)',
                              borderRadius: '999px',
                              padding: '0.15rem 0.55rem',
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                            }}
                          >
                            New
                          </span>
                        )}
                      </div>
                      <p
                        style={{
                          margin: '0.25rem 0 0 0',
                          color: 'var(--liwaza-on-surface-variant)',
                          fontSize: '0.9rem',
                        }}
                      >
                        {category.tips.length} strategies
                      </p>
                    </div>
                  </div>

                  <div
                    style={{
                      color: 'var(--liwaza-on-surface-variant)',
                      flexShrink: 0,
                    }}
                  >
                    {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                  </div>
                </button>

                {isExpanded && (
                  <div
                    style={{
                      padding: '0 1.25rem 1.25rem 1.25rem',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                      gap: '1rem',
                    }}
                  >
                    {category.tips.map((tip, tipIndex) => {
                      const priorityStyles = getPriorityStyles(tip);

                      return (
                        <div
                          key={`${tip.title}-${tipIndex}`}
                          style={{
                            background: 'var(--liwaza-surface)',
                            border: '1px solid var(--liwaza-outline-variant)',
                            borderRadius: '14px',
                            padding: '1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.75rem',
                            minHeight: '100%',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: '0.75rem',
                            }}
                          >
                            <h4
                              style={{
                                margin: 0,
                                color: 'var(--liwaza-on-surface)',
                                fontSize: '0.98rem',
                                lineHeight: 1.35,
                              }}
                            >
                              {tip.title}
                            </h4>
                            <span
                              style={{
                                ...priorityStyles,
                                borderRadius: '999px',
                                padding: '0.2rem 0.55rem',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {getPriorityLabel(tip)}
                            </span>
                          </div>

                          <p
                            style={{
                              margin: 0,
                              color: 'var(--liwaza-on-surface-variant)',
                              fontSize: '0.9rem',
                              lineHeight: 1.58,
                            }}
                          >
                            {tip.content}
                          </p>

                          {tip.example && (
                            <div
                              style={{
                                background: 'var(--liwaza-surface-container)',
                                borderLeft: '3px solid var(--liwaza-primary)',
                                borderRadius: '10px',
                                padding: '0.85rem 0.95rem',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '0.75rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.08em',
                                  color: 'var(--liwaza-primary)',
                                  fontWeight: 700,
                                  marginBottom: '0.35rem',
                                }}
                              >
                                Example
                              </div>
                              <p
                                style={{
                                  margin: 0,
                                  color: 'var(--liwaza-on-surface-variant)',
                                  fontSize: '0.84rem',
                                  lineHeight: 1.55,
                                }}
                              >
                                {tip.example}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
